import React, { useState, useEffect, forwardRef, useRef } from "react";
import CategoryManager from "../../../services/home/CategoryManager";
import { handleApiResponse } from "../../../utils/apiResponseHandler";
import { ChevronLeft, ChevronRight } from "lucide-react";
import config from "../../../config";
import { motion } from "framer-motion";

const CategoryFilter = forwardRef(({ onSelect = () => {}, selectedCategory = null }, ref) => {
  const [categories, setCategories] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await CategoryManager.getAllCategories();
        const categoriesList = handleApiResponse(data);
        setCategories(categoriesList || []);
      } catch (err) {
        console.error("Failed to load categories", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    setSelectedId(selectedCategory?.catId ?? null);
  }, [selectedCategory]);

  const handleCategoryClick = (cat) => {
    if (selectedId === cat.catId) {
      setSelectedId(null);
      onSelect(null);
    } else {
      setSelectedId(cat.catId);
      onSelect(cat);
    }
  };

  const scrollByAmount = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: dir * 260,
      behavior: "smooth",
    });
  };

  if (loading) {
    return (
      <div className="py-3" ref={ref}>
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 animate-pulse rounded-full bg-gray-200" />
          <div className="flex flex-1 gap-3 overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-11 w-44 shrink-0 animate-pulse rounded-full bg-gray-200" />
            ))}
          </div>
          <div className="h-14 w-14 animate-pulse rounded-full bg-gray-200" />
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="py-2" ref={ref}>
        <div
          className="category-rail flex gap-2 overflow-x-auto"
          style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {categories.map((cat) => {
            const active = selectedId === cat.catId;
            return (
              <button
                key={cat.catId}
                onClick={() => handleCategoryClick(cat)}
                className={`relative inline-flex shrink-0 items-center gap-2 overflow-hidden rounded-full border px-4 py-2 text-sm font-medium transition-all duration-500 ease-out ${
                  active
                    ? "border-transparent text-white shadow-[0_8px_18px_-10px_rgba(255,122,20,0.95)]"
                    : "bg-white text-[#3b4d63] border border-[#e4e6eb]"
                }`}
              >
                {active ? (
                  <motion.span
                    layoutId="category-pill-active-mobile"
                    className="absolute inset-0 rounded-full bg-[#ff7a14]"
                    transition={{ type: "spring", stiffness: 430, damping: 36, mass: 0.9 }}
                  />
                ) : null}
                {cat.iconUrl ? (
                  <img
                    src={`${config.url}/categoryIcons/${cat.iconUrl}`}
                    alt={cat.catName}
                    className={`relative z-10 h-4 w-4 object-contain transition ${
                      active ? "brightness-0 invert" : ""
                    }`}
                  />
                ) : null}
                <span className="relative z-10">{cat.catName}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="py-3" ref={ref}>
      <div className="flex items-center gap-4">
        <button
          onClick={() => scrollByAmount(-1)}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-[#ff7a14] bg-white text-[#ff7a14] shadow-[0_6px_18px_-10px_rgba(0,0,0,0.55)] transition hover:scale-[1.02] hover:bg-primary hover:text-white"
          aria-label="Scroll categories left"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div
          ref={scrollRef}
          className="category-rail flex flex-1 items-center gap-3 overflow-x-auto"
          style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {categories.map((cat) => {
            const active = selectedId === cat.catId;
            return (
              <button
                key={cat.catId}
                onClick={() => handleCategoryClick(cat)}
                className={`relative inline-flex shrink-0 items-center gap-2 overflow-hidden rounded-full border px-7 py-2.5 text-[30px] font-medium leading-none transition-all duration-500 ease-out ${
                  active
                    ? "border-transparent text-white shadow-[0_10px_22px_-12px_rgba(255,122,20,0.98)]"
                    : "bg-white text-[#3b4d63] border border-[#e4e6eb] hover:bg-[#f7f7f8]"
                }`}
                style={{ fontSize: "18px" }}
              >
                {active ? (
                  <motion.span
                    layoutId="category-pill-active-desktop"
                    className="absolute inset-0 rounded-full bg-[#ff7a14]"
                    transition={{ type: "spring", stiffness: 430, damping: 36, mass: 0.9 }}
                  />
                ) : null}
                {cat.iconUrl ? (
                  <img
                    src={`${config.url}/categoryIcons/${cat.iconUrl}`}
                    alt={cat.catName}
                    className={`relative z-10 h-5 w-5 object-contain transition ${
                      active ? "brightness-0 invert" : ""
                    }`}
                  />
                ) : null}
                <span className="relative z-10">{cat.catName}</span>
              </button>
            );
          })}
        </div>

        <button
          onClick={() => scrollByAmount(1)}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-[#ff7a14] bg-white text-[#ff7a14] shadow-[0_6px_18px_-10px_rgba(0,0,0,0.55)] transition hover:scale-[1.02] hover:bg-primary hover:text-white"
          aria-label="Scroll categories right"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <style>{`
        .category-rail::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
});

export default CategoryFilter;
